import React, { useMemo, useState } from 'react';
import { useStore } from 'zustand';

import type { PackExperiencesResult, Room } from '../../ExtranetPackages.types';
import { PackType, packagesStore } from '../../PackageStore';
import { RoomModal } from '../RoomModal/RoomModal';
import type { AddOn } from '../Tables/AddOnsTable/AddOnsTable';
import { AddOnsTable } from '../Tables/AddOnsTable/AddOnsTable';
import { ExperiencesTable } from '../Tables/ExperiencesTable/ExperiencesTable';
import type { PricingTotals } from '../Tables/ExperiencesTable/PricingTotals.types';
import { RoomsTable } from '../Tables/RoomsTable/RoomsTable';

type PackagesTablesProps = {
  experiences?: PackExperiencesResult;
  pricingTotals?: PricingTotals;
  addOns?: AddOn[];
  rooms?: Room[];
  loading?: boolean;
  minChargedPax?: number;
  maxAllowedPax?: number;
};

export const PackagesTables: React.FC<PackagesTablesProps> = ({
  experiences = { alwaysIncludedXps: [], includedExperience: [] },
  pricingTotals,
  addOns = [],
  rooms,
  loading,
  minChargedPax,
  maxAllowedPax,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | undefined>();
  const packType = useStore(packagesStore, (state) => state.packType);

  const isDaytime = packType === PackType.DAYTIME;

  const hasChildPrices = useMemo(
    () =>
      experiences.alwaysIncludedXps.some(
        (exp) => exp.childPrice !== undefined && exp.childPrice !== null
      ) ||
      experiences.includedExperience.some(
        (exp) => exp.childPrice !== undefined && exp.childPrice !== null
      ) ||
      addOns.some(
        (addon) => addon.childPrice !== undefined && addon.childPrice !== null
      ) ||
      experiences.alwaysIncludedXps.some(
        (exp) => exp.areChildrenAllowed !== false
      ) ||
      experiences.includedExperience.some(
        (exp) => exp.areChildrenAllowed !== false
      ) ||
      addOns.some((addon) => addon.areChildrenAllowed !== false),

    [experiences, addOns]
  );

  const handleViewRoom = (room: Room) => {
    setSelectedRoom(room);
    setIsModalOpen(true);
  };

  return (
    <>
      <ExperiencesTable
        dataSource={experiences}
        loading={loading}
        pricingTotals={pricingTotals}
        hideChildPriceColumn={!hasChildPrices}
        minChargedPax={minChargedPax}
        maxAllowedPax={maxAllowedPax}
        subHeader={isDaytime ? undefined : 'Included in this package'}
        subHeaderOvernight={
          isDaytime ? undefined : 'Included in all overnight packages'
        }
      />
      <AddOnsTable
        dataSource={addOns}
        loading={loading}
        hideChildPriceColumn={!hasChildPrices}
      />
      {rooms !== undefined && (
        <>
          <RoomsTable
            dataSource={rooms}
            loading={loading}
            onViewRoom={handleViewRoom}
          />
          <RoomModal
            isModalOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedRoom(undefined);
            }}
            room={selectedRoom}
          />
        </>
      )}
    </>
  );
};
